// src/components/Control/Operator/EnergyChart.tsx
// Питание за период (Настройки → Диагностика): батарея (В), ток (мА) и питание Pi (5 В),
// которые нода energy_log на Pi пишет постоянно (раз в 10 с), смотрят на сайт или нет.
//   robot/<id>/energy/history/<часы>  get -> {"columns": [...], "rows": [[...], ...]} (не больше 600 точек)
// Три графика с общей осью времени (у величин разные шкалы — двух осей Y не делаем),
// у каждого линия среднего и полоса мин–макс; общая перекрестная линия и сводка при наведении.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as zenoh from '@eclipse-zenoh/zenoh-ts';
import { useRobot } from '../robot/RobotContext';

const RANGES: [number, string][] = [[1, '1 ч'], [6, '6 ч'], [24, '24 ч'], [24 * 7, '7 дн'], [24 * 30, '30 дн']];
const SERIES = '#2a78d6';     // одна серия на график — один цвет
const CRITICAL = '#d03b3b';   // статус: просадка питания Pi (всегда со значком и подписью)

type Row = Record<string, number | null>;

async function fetchHistory(session: zenoh.Session, key: string): Promise<Row[]> {
  const rx = await session.get(key);
  if (!rx) throw new Error('запрос не отправлен');
  for await (const reply of rx) {
    const r = reply.result();
    if (!(r instanceof zenoh.Sample)) throw new Error(r.payload().toString());
    const msg = JSON.parse(r.payload().toString()) as { columns: string[]; rows: (number | null)[][] };
    return msg.rows.map(row => Object.fromEntries(msg.columns.map((c, i) => [c, row[i]])));
  }
  throw new Error('робот не ответил (нода energy_log не запущена?)');
}

function useWidth(ref: React.RefObject<HTMLElement | null>) {
  const [w, setW] = useState(600);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => setW(Math.max(260, Math.floor(entries[0].contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return w;
}

const timeLabel = (t: number, hours: number) => {
  const d = new Date(t * 1000);
  return hours > 24
    ? d.toLocaleDateString([], { day: '2-digit', month: '2-digit' }) + (hours <= 24 * 7 ? ` ${d.getHours()}:00` : '')
    : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

function niceTicks(lo: number, hi: number, n = 3): number[] {
  const span = hi - lo || 1;
  const raw = span / n;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map(k => k * mag).find(s => s >= raw) ?? raw;
  const out: number[] = [];
  for (let v = Math.ceil(lo / step) * step; v <= hi + 1e-9; v += step) out.push(Math.round(v * 1e6) / 1e6);
  return out;
}

interface PanelProps {
  title: string;
  unit: string;
  rows: Row[];
  avg: string;
  low?: string;
  high?: string;
  digits: number;
  width: number;
  t0: number;
  t1: number;
  gap: number;            // разрыв линии, если между точками дольше (робот был выключен)
  hover: number | null;   // индекс строки под курсором
  onHover: (i: number | null) => void;
  markers?: string;       // колонка-флаг: отметки внизу
  markerLabel?: string;
  ref_?: { value: number; label: string };
}

const H = 120, PAD_L = 46, PAD_R = 10, PAD_T = 8, PAD_B = 18;

const Panel: React.FC<PanelProps> = p => {
  const { rows, width, t0, t1 } = p;
  const vals = rows.flatMap(r => [r[p.avg], p.low ? r[p.low] : null, p.high ? r[p.high] : null])
    .filter((v): v is number => v != null);
  if (p.ref_) vals.push(p.ref_.value);
  const has = rows.some(r => r[p.avg] != null);
  let lo = Math.min(...vals), hi = Math.max(...vals);
  if (!isFinite(lo)) { lo = 0; hi = 1; }
  const pad = (hi - lo) * 0.1 || Math.abs(hi) * 0.02 || 1;
  lo -= pad;
  hi += pad;
  const x = (t: number) => PAD_L + ((t - t0) / (t1 - t0 || 1)) * (width - PAD_L - PAD_R);
  const y = (v: number) => PAD_T + (1 - (v - lo) / (hi - lo)) * (H - PAD_T - PAD_B);

  // отрезки без разрывов
  const segs: Row[][] = [];
  rows.forEach((r, i) => {
    if (r[p.avg] == null) return;
    const prev = i > 0 ? rows[i - 1] : null;
    if (!segs.length || !prev || prev[p.avg] == null || (r.t as number) - (prev.t as number) > p.gap) segs.push([]);
    segs[segs.length - 1].push(r);
  });
  const line = (seg: Row[]) => seg.map((r, i) => `${i ? 'L' : 'M'}${x(r.t!).toFixed(1)},${y(r[p.avg]!).toFixed(1)}`).join('');
  const band = (seg: Row[]) => {
    if (!p.low && !p.high) return '';
    const top = seg.map(r => `${x(r.t!).toFixed(1)},${y((p.high ? r[p.high] : r[p.avg]) ?? r[p.avg]!).toFixed(1)}`);
    const bot = seg.slice().reverse().map(r => `${x(r.t!).toFixed(1)},${y((p.low ? r[p.low] : r[p.avg]) ?? r[p.avg]!).toFixed(1)}`);
    return `M${top.join('L')}L${bot.join('L')}Z`;
  };
  const hr = p.hover != null ? rows[p.hover] : null;
  const pick = (e: React.PointerEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const t = t0 + ((e.clientX - rect.left - PAD_L) / (rect.width - PAD_L - PAD_R)) * (t1 - t0);
    let best = -1, bd = Infinity;
    rows.forEach((r, i) => {
      const d = Math.abs((r.t as number) - t);
      if (d < bd) { bd = d; best = i; }
    });
    p.onHover(best >= 0 ? best : null);
  };

  return (
    <figure className="op-chart">
      <figcaption>{p.title}</figcaption>
      <svg width={width} height={H} role="img" aria-label={p.title} onPointerMove={pick} onPointerLeave={() => p.onHover(null)}>
        {niceTicks(lo, hi).map(v => (
          <g key={v}>
            <line x1={PAD_L} x2={width - PAD_R} y1={y(v)} y2={y(v)} className="op-chart-grid" />
            <text x={PAD_L - 4} y={y(v) + 3} textAnchor="end" className="op-chart-tick">{v}</text>
          </g>
        ))}
        {p.ref_ && (
          <g>
            <line x1={PAD_L} x2={width - PAD_R} y1={y(p.ref_.value)} y2={y(p.ref_.value)} className="op-chart-ref" />
            <text x={width - PAD_R} y={y(p.ref_.value) - 3} textAnchor="end" className="op-chart-tick">{p.ref_.label}</text>
          </g>
        )}
        {segs.map((seg, k) => (
          <g key={k}>
            {(p.low || p.high) && <path d={band(seg)} fill={SERIES} fillOpacity={0.16} />}
            <path d={line(seg)} fill="none" stroke={SERIES} strokeWidth={2} strokeLinejoin="round" />
          </g>
        ))}
        {p.markers && rows.map((r, i) => (r[p.markers!] ? (
          <text key={i} x={x(r.t!)} y={H - PAD_B - 2} textAnchor="middle" fontSize={10} fill={CRITICAL}>▲</text>
        ) : null))}
        {!has && <text x={width / 2} y={H / 2} textAnchor="middle" className="op-chart-tick">нет данных за период</text>}
        {hr && (
          <g pointerEvents="none">
            <line x1={x(hr.t!)} x2={x(hr.t!)} y1={PAD_T} y2={H - PAD_B} className="op-chart-cross" />
            {hr[p.avg] != null && <circle cx={x(hr.t!)} cy={y(hr[p.avg]!)} r={4} fill={SERIES} stroke="#fff" strokeWidth={2} />}
          </g>
        )}
        <line x1={PAD_L} x2={width - PAD_R} y1={H - PAD_B} y2={H - PAD_B} className="op-chart-axis" />
      </svg>
    </figure>
  );
};

const EnergyChart: React.FC = () => {
  const { session, robotId } = useRobot();
  const [hours, setHours] = useState(24);
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const width = useWidth(box);

  const load = useCallback(async () => {
    if (!session) return;
    try {
      setRows(await fetchHistory(session, `robot/${robotId}/energy/history/${hours}`));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [session, robotId, hours]);

  useEffect(() => {
    setRows(null);
    setHover(null);
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const { t0, t1, gap } = useMemo(() => {
    const now = Date.now() / 1000;
    const t0 = now - hours * 3600;
    // точки идут раз в 10 с, при прореживании — реже: разрыв, если пропущено больше 3 шагов
    const step = rows && rows.length > 1 ? (rows[rows.length - 1].t! - rows[0].t!) / (rows.length - 1) : 10;
    return { t0, t1: now, gap: Math.max(30, step * 3) };
  }, [rows, hours]);

  const shown = hover != null && rows ? rows[hover] : rows?.[rows.length - 1] ?? null;
  const dips = rows ? rows.filter(r => r.undervolt).length : 0;

  const csv = () => {
    if (!rows) return;
    const cols = Object.keys(rows[0] ?? {});
    const text = [cols.join(','), ...rows.map(r => cols.map(c => (c === 't' ? new Date(r.t! * 1000).toISOString() : r[c] ?? '')).join(','))].join('\n');
    const url = URL.createObjectURL(new Blob([text], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `robot-${robotId}-energy-${hours}h.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const f = (v: number | null | undefined, d: number, unit: string) => (v == null ? '—' : `${v.toFixed(d)} ${unit}`);

  return (
    <div className="feature-card op-energy" ref={box}>
      <h3><span>Питание</span></h3>
      <div className="row" role="group" aria-label="Период">
        {RANGES.map(([h, label]) => (
          <button key={h} type="button" className={`op-btn ${h === hours ? 'primary' : ''}`} aria-pressed={h === hours}
                  onClick={() => setHours(h)}>{label}</button>
        ))}
        <button type="button" className="op-btn" onClick={load} title="Обновить сейчас (само — раз в 30 с)">↻</button>
      </div>
      {error && <p className="error-text">{error}</p>}
      {!rows && !error && <p className="hint">Загружаю…</p>}
      {rows && (
        <>
          <div className="op-energy-readout" aria-live="off">
            {shown ? (
              <>
                <span className="when">{new Date(shown.t! * 1000).toLocaleString()}</span>
                <span><b>{f(shown.v_avg, 2, 'В')}</b> батарея{shown.v_min != null && shown.v_max != null ? ` (${shown.v_min.toFixed(2)}–${shown.v_max.toFixed(2)})` : ''}</span>
                <span><b>{f(shown.i_avg, 0, 'мА')}</b> ток{shown.i_max != null ? ` (пик ${shown.i_max.toFixed(0)})` : ''}</span>
                <span><b>{f(shown.soc, 0, '%')}</b> заряд</span>
                <span><b>{f(shown.pi5v_min, 2, 'В')}</b> Pi 5 В (мин.)</span>
                <span><b>{f(shown.pi_w, 1, 'Вт')}</b> Pi</span>
                {shown.undervolt ? <span className="op-energy-dip">⚠ просадка питания Pi</span> : null}
                {shown.asleep ? <span>💤 спал</span> : null}
              </>
            ) : <span>Точек за период нет.</span>}
          </div>
          <Panel title="Батарея, В (линия — среднее, полоса — мин–макс за 10 с)" unit="В" rows={rows} avg="v_avg" low="v_min" high="v_max" digits={2}
                 width={width - 24} t0={t0} t1={t1} gap={gap} hover={hover} onHover={setHover} />
          <Panel title="Ток от батареи, мА (полоса — до пика)" unit="мА" rows={rows} avg="i_avg" high="i_max" digits={0}
                 width={width - 24} t0={t0} t1={t1} gap={gap} hover={hover} onHover={setHover} />
          <Panel title="Питание Pi, В (EXT5V; ▲ — просадка ниже порога)" unit="В" rows={rows} avg="pi5v_avg" low="pi5v_min" digits={2}
                 width={width - 24} t0={t0} t1={t1} gap={gap} hover={hover} onHover={setHover} markers="undervolt"
                 ref_={{ value: 4.75, label: '4,75 В' }} />
          <div className="op-chart-x" style={{ paddingLeft: PAD_L }}>
            {[0, 0.25, 0.5, 0.75, 1].map(k => <span key={k}>{timeLabel(t0 + k * (t1 - t0), hours)}</span>)}
          </div>
          <p className={dips ? 'error-text' : 'hint'}>
            {dips ? `⚠ Просадки питания Pi: в ${dips} из ${rows.length} точек — блок питания или провод не тянут (нужно 5 В / 5 А).`
              : 'Просадок питания Pi за период не было.'}
            {' '}Пишется на Pi постоянно, раз в 10 с; хранится 60 дней (~/fleetcore-logs/energy).
          </p>
          <details>
            <summary>Таблица (последние 50 точек)</summary>
            <table className="op-table">
              <thead><tr><th>Время</th><th>Батарея, В</th><th>Ток, мА</th><th>Заряд</th><th>Pi 5 В мин.</th><th>Pi, Вт</th><th>Просадка</th></tr></thead>
              <tbody>
                {rows.slice(-50).reverse().map(r => (
                  <tr key={r.t!}>
                    <td>{new Date(r.t! * 1000).toLocaleString()}</td>
                    <td>{r.v_avg?.toFixed(2) ?? '—'}</td>
                    <td>{r.i_avg?.toFixed(0) ?? '—'}</td>
                    <td>{r.soc != null ? `${r.soc.toFixed(0)}%` : '—'}</td>
                    <td>{r.pi5v_min?.toFixed(2) ?? '—'}</td>
                    <td>{r.pi_w?.toFixed(1) ?? '—'}</td>
                    <td>{r.undervolt ? '⚠ да' : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button type="button" className="op-btn" onClick={csv}>Скачать CSV за период</button>
          </details>
        </>
      )}
    </div>
  );
};

export default EnergyChart;
